/**
 * client/ui/element-skins-modal.ts
 * Responsabilidad: Gestor del Modal de la Tienda de Skins Elementales.
 * Filtros por los 5 elementos, catálogo autoritativo con fallback, compra dual (💎 + 🔮)
 * y equipamiento en tiempo real sincronizado con el previsualizador y Colyseus.
 */
import type { Room } from "@colyseus/sdk";
import Swal from "sweetalert2";
import {
    type ElementSkinDef,
    FALLBACK_ELEMENT_SKINS,
    getDefaultSkinForElement,
} from "../element-skins";
import { questManager } from "../quests";
import { safeGetStorage, safeSetStorage } from "../utils";
import type { MenuManager } from "./menu";

export class ElementSkinsModalManager {
    private modalEl: HTMLElement | null = null;
    private overlayEl: HTMLElement | null = null;
    private closeBtn: HTMLButtonElement | null = null;
    private orbsCountEl: HTMLElement | null = null;
    private crystalsCountEl: HTMLElement | null = null;
    private cardsGridEl: HTMLElement | null = null;
    private filterButtons: NodeListOf<HTMLButtonElement> | null = null;
    private activeElement: string = "water";
    private catalog: ElementSkinDef[] = [...FALLBACK_ELEMENT_SKINS];
    private unlockedSkins: string[] = [
        "agua_001",
        "electrico_001",
        "fuego_001",
        "planta_001",
        "tierra_001",
    ];
    private equippedSkins: Record<string, string> = {
        water: "agua_001",
        electric: "electrico_001",
        fire: "fuego_001",
        plant: "planta_001",
        earth: "tierra_001",
    };
    private astralOrbs: number = 0;
    private crystals: number = 0;
    private openGraceTimestamp: number = 0;
    private menu: MenuManager | null = null;
    private room: Room | null = null;

    constructor() {
        this.initDomReferences();
        this.bindEvents();
        void this.fetchCatalog();
        this.restoreLocalEquipped();
        if (typeof window !== "undefined") {
            window.addEventListener("alchem:auth_change", () => {
                void this.refreshUserData();
            });
        }
    }

    public setMenu(menu: MenuManager): void {
        this.menu = menu;
        this.restoreLocalEquipped();
    }

    public setRoom(room: Room | null): void {
        this.room = room;
    }

    private getAuthToken(): string | null {
        return safeGetStorage("alchem_auth_token") || safeGetStorage("colyseus-auth-token") || null;
    }

    private initDomReferences(): void {
        this.modalEl = document.getElementById("element-skins-modal");
        this.overlayEl = document.querySelector(".element-skins-modal-overlay");
        this.closeBtn = document.getElementById("element-skins-modal-close") as HTMLButtonElement | null;
        this.orbsCountEl = document.getElementById("element-skins-orbs-count");
        this.crystalsCountEl = document.getElementById("element-skins-crystals-count");
        this.cardsGridEl = document.getElementById("element-skins-cards-grid");
        this.filterButtons = document.querySelectorAll<HTMLButtonElement>(".element-skin-filter-btn");
    }

    private bindEvents(): void {
        if (this.closeBtn) {
            this.closeBtn.addEventListener("click", () => this.close());
        }
        if (this.overlayEl) {
            this.overlayEl.addEventListener("click", () => {
                if (Date.now() - this.openGraceTimestamp < 100) return;
                this.close();
            });
        }
        window.addEventListener("keydown", (e: KeyboardEvent) => {
            if (e.key === "Escape" && this.isOpen()) {
                this.close();
            }
        });
        if (this.filterButtons) {
            this.filterButtons.forEach((btn) => {
                btn.addEventListener("click", () => {
                    const el = btn.dataset.element || "water";
                    this.activeElement = el;
                    this.updateActiveFilterTab();
                    this.renderCards();
                });
            });
        }
    }

    private restoreLocalEquipped(): void {
        try {
            const saved = safeGetStorage("alchem_cosmetics");
            if (saved) {
                const parsed = JSON.parse(saved);
                if (parsed.elementSkins && typeof parsed.elementSkins === "object") {
                    this.equippedSkins = { ...this.equippedSkins, ...parsed.elementSkins };
                }
            }
            const unlocked = safeGetStorage("alchem_unlocked_skins");
            if (unlocked) {
                const list = JSON.parse(unlocked);
                if (Array.isArray(list)) {
                    this.unlockedSkins = Array.from(new Set([...this.unlockedSkins, ...list]));
                }
            }
        } catch {}
    }

    public isOpen(): boolean {
        return !!this.modalEl && !this.modalEl.classList.contains("hidden");
    }

    public open(initialElement?: string): void {
        if (!this.modalEl) {
            this.initDomReferences();
            this.bindEvents();
        }
        if (!this.modalEl) return;
        this.openGraceTimestamp = Date.now();
        if (initialElement) {
            this.activeElement = initialElement;
        }
        this.modalEl.classList.remove("hidden");
        document.body.classList.add("modal-open");
        void this.refreshUserData();
        this.updateActiveFilterTab();
        this.renderCards();
    }

    public close(): void {
        if (!this.modalEl) return;
        this.modalEl.classList.add("hidden");
        document.body.classList.remove("modal-open");
    }

    public async fetchCatalog(): Promise<void> {
        try {
            const res = await fetch("/api/shop/element-skins");
            if (res.ok) {
                const data = await res.json();
                if (data && data.success && Array.isArray(data.catalog) && data.catalog.length > 0) {
                    this.catalog = data.catalog;
                }
            }
        } catch {
            this.catalog = [...FALLBACK_ELEMENT_SKINS];
        }
        if (this.isOpen()) {
            this.renderCards();
        }
    }

    public async refreshUserData(): Promise<void> {
        const token = this.getAuthToken();
        this.astralOrbs = questManager.getAstralOrbs();
        this.crystals = questManager.getCrystals();
        this.updateBalancesDisplay();
        if (this.menu) {
            const cosmetics = this.menu.getCosmetics();
            if (cosmetics.elementSkins) {
                this.equippedSkins = { ...this.equippedSkins, ...cosmetics.elementSkins };
            }
        }
        if (!token) {
            this.renderCards();
            return;
        }
        try {
            const res = await fetch("/api/shop/element-skins/status", {
                headers: { Authorization: `Bearer ${token}` },
            });
            if (res.ok) {
                const data = await res.json();
                if (data && data.success) {
                    if (Array.isArray(data.unlockedSkins)) {
                        this.unlockedSkins = Array.from(new Set([...this.unlockedSkins, ...data.unlockedSkins]));
                        safeSetStorage("alchem_unlocked_skins", JSON.stringify(this.unlockedSkins));
                    }
                    if (data.equippedSkins && typeof data.equippedSkins === "object") {
                        this.equippedSkins = { ...this.equippedSkins, ...data.equippedSkins };
                    }
                    if (typeof data.crystals === "number" || typeof data.astralOrbs === "number") {
                        this.crystals = data.crystals ?? this.crystals;
                        this.astralOrbs = data.astralOrbs ?? this.astralOrbs;
                        questManager.updateBalances(this.crystals, this.astralOrbs);
                        this.updateBalancesDisplay();
                    }
                }
            }
        } catch (err) {
            console.warn("[ElementSkinsModal] Error al consultar /api/shop/element-skins/status:", err);
        }
        this.renderCards();
    }

    private updateBalancesDisplay(): void {
        if (this.orbsCountEl) {
            this.orbsCountEl.textContent = `🔮 ${this.astralOrbs}`;
        }
        if (this.crystalsCountEl) {
            this.crystalsCountEl.textContent = `💎 ${this.crystals}`;
        }
    }

    private updateActiveFilterTab(): void {
        if (!this.filterButtons) return;
        this.filterButtons.forEach((btn) => {
            if (btn.dataset.element === this.activeElement) {
                btn.classList.add("active");
            } else {
                btn.classList.remove("active");
            }
        });
    }

    public renderCards(): void {
        const grid = this.cardsGridEl;
        if (!grid) return;
        grid.innerHTML = "";
        const skinsForElement = this.catalog.filter((s) => s.element === this.activeElement);
        skinsForElement.sort((a, b) => a.tier - b.tier);
        const currentEquippedSkin = this.equippedSkins[this.activeElement] || getDefaultSkinForElement(this.activeElement);
        skinsForElement.forEach((skin) => {
            const isEquipped = currentEquippedSkin === skin.id;
            const isUnlocked = skin.tier === 1 || this.unlockedSkins.includes(skin.id);
            let ribbonClass = "skin-ribbon-base";
            let ribbonText = "🌱 BASE";
            if (skin.tier === 2) {
                ribbonClass = "skin-ribbon-rare";
                ribbonText = "✨ RARO";
            } else if (skin.tier === 3) {
                ribbonClass = "skin-ribbon-epic";
                ribbonText = "⚡ ÉPICO";
            } else if (skin.tier === 4) {
                ribbonClass = "skin-ribbon-legendary";
                ribbonText = "👑 LEGENDARIO";
            } else if (skin.tier === 5) {
                ribbonClass = "skin-ribbon-divine";
                ribbonText = "🔥 DIVINO";
            }
            const tierRoman = ["Tier I", "Tier II", "Tier III", "Tier IV", "Tier V"][skin.tier - 1] || `Tier ${skin.tier}`;
            const card = document.createElement("div");
            card.className = `element-skin-card tier-${skin.tier} ${isEquipped ? "is-equipped" : ""}`;
            let priceHtml = "";
            if (skin.tier === 1) {
                priceHtml = `<div class="element-skin-price is-free">Gratis (Base)</div>`;
            } else {
                priceHtml = `<div class="element-skin-price">🔮 ${skin.priceOrbs} + 💎 ${skin.priceCrystals}</div>`;
            }
            let actionBtnHtml = "";
            if (isEquipped) {
                actionBtnHtml = `<button type="button" class="element-skin-action-btn element-skin-btn-equipped" disabled>Equipada ✨</button>`;
            } else if (isUnlocked) {
                actionBtnHtml = `<button type="button" class="element-skin-action-btn element-skin-btn-equip" data-id="${skin.id}">Equipar</button>`;
            } else {
                actionBtnHtml = `<button type="button" class="element-skin-action-btn element-skin-btn-buy" data-id="${skin.id}">Desbloquear</button>`;
            }
            card.innerHTML = `
                <div class="element-skin-ribbon ${ribbonClass}">${ribbonText}</div>
                <div class="element-skin-tier-badge">${tierRoman}</div>
                <div class="element-skin-img-wrap">
                    <img src="assets/SkinsElementales/${skin.id}.png" alt="${skin.name}" class="element-skin-img" loading="lazy">
                </div>
                <div class="element-skin-name">${skin.name}</div>
                ${priceHtml}
                ${actionBtnHtml}
            `;
            const equipBtn = card.querySelector(".element-skin-btn-equip");
            if (equipBtn) {
                equipBtn.addEventListener("click", () => {
                    void this.handleEquip(skin.id);
                });
            }
            const buyBtn = card.querySelector(".element-skin-btn-buy");
            if (buyBtn) {
                buyBtn.addEventListener("click", () => {
                    void this.handleBuy(skin);
                });
            }
            grid.appendChild(card);
        });
    }

    private async handleBuy(skin: ElementSkinDef): Promise<void> {
        const token = this.getAuthToken();
        if (!token) {
            Swal.fire({
                title: "Inicia Sesión",
                text: "Debes iniciar sesión para desbloquear esta skin elemental y conservarla para siempre.",
                icon: "info",
                confirmButtonText: "Entendido",
                confirmButtonColor: "#38bdf8",
            });
            return;
        }
        const userCrystals = questManager.getCrystals();
        const userOrbs = questManager.getAstralOrbs();
        if (userOrbs < skin.priceOrbs || userCrystals < skin.priceCrystals) {
            Swal.fire({
                title: "Fondos Insuficientes",
                html: `Necesitas <b>🔮 ${skin.priceOrbs} Orbes</b> y <b>💎 ${skin.priceCrystals} Cristales</b> para desbloquear <b>${skin.name}</b>.<br><br>Saldo actual:<br>🔮 ${userOrbs} Orbes<br>💎 ${userCrystals} Cristales.`,
                icon: "warning",
                confirmButtonText: "Aceptar",
                confirmButtonColor: "#f59e0b",
            });
            return;
        }
        const confirm = await Swal.fire({
            title: `¿Desbloquear ${skin.name}?`,
            html: `Se descontarán <b>🔮 ${skin.priceOrbs} Orbes</b> y <b>💎 ${skin.priceCrystals} Cristales</b> de tu reserva.`,
            imageUrl: `assets/SkinsElementales/${skin.id}.png`,
            imageWidth: 96,
            imageHeight: 96,
            imageAlt: skin.name,
            showCancelButton: true,
            confirmButtonText: "¡Sí, Desbloquear!",
            cancelButtonText: "Cancelar",
            confirmButtonColor: "#f59e0b",
            cancelButtonColor: "#64748b",
        });
        if (!confirm.isConfirmed) return;
        try {
            const res = await fetch("/api/shop/element-skins/buy", {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${token}`,
                },
                body: JSON.stringify({ skinId: skin.id }),
            });
            const data = await res.json();
            if (!res.ok || !data.success) {
                Swal.fire({
                    title: "Error",
                    text: data.message || "No se pudo completar la compra.",
                    icon: "error",
                    confirmButtonColor: "#ef4444",
                });
                return;
            }
            if (Array.isArray(data.unlockedSkins)) {
                this.unlockedSkins = data.unlockedSkins;
                safeSetStorage("alchem_unlocked_skins", JSON.stringify(this.unlockedSkins));
            } else if (!this.unlockedSkins.includes(skin.id)) {
                this.unlockedSkins.push(skin.id);
                safeSetStorage("alchem_unlocked_skins", JSON.stringify(this.unlockedSkins));
            }
            if (typeof data.astralOrbs === "number" || typeof data.crystals === "number") {
                this.astralOrbs = data.astralOrbs ?? this.astralOrbs;
                this.crystals = data.crystals ?? this.crystals;
                questManager.updateBalances(this.crystals, this.astralOrbs);
                this.updateBalancesDisplay();
            }
            // Auto-equipar la nueva skin adquirida
            await this.handleEquip(skin.id, false);
            Swal.fire({
                title: "¡Skin Desbloqueada!",
                text: data.message || `Has desbloqueado a ${skin.name} con éxito.`,
                icon: "success",
                confirmButtonText: "¡Excelente!",
                confirmButtonColor: "#38bdf8",
            });
        } catch (err) {
            console.error("[ElementSkinsModal] Error al comprar skin:", err);
            Swal.fire({
                title: "Error de Red",
                text: "No se pudo conectar con el servidor.",
                icon: "error",
                confirmButtonColor: "#ef4444",
            });
        }
    }

    public async handleEquip(skinId: string, showToast: boolean = true): Promise<void> {
        this.equippedSkins[this.activeElement] = skinId;
        // 1. Sincronizar en MenuManager y previsualizador
        if (this.menu) {
            this.menu.setElementSkin(this.activeElement, skinId);
        }
        // 2. Transmitir por Colyseus si room está conectado
        if (this.room) {
            try {
                const cosmetics = this.menu ? this.menu.getCosmetics() : { elementSkins: this.equippedSkins };
                this.room.send("set-skin", { skin: JSON.stringify(cosmetics) });
            } catch {}
        }
        // 3. Persistir en backend si hay sesión iniciada
        const token = this.getAuthToken();
        if (token) {
            try {
                await fetch("/api/shop/element-skins/equip", {
                    method: "POST",
                    headers: {
                        "Content-Type": "application/json",
                        Authorization: `Bearer ${token}`,
                    },
                    body: JSON.stringify({ skinId }),
                });
            } catch {}
        }
        this.renderCards();
        if (showToast) {
            const skinDef = this.catalog.find((s) => s.id === skinId);
            const name = skinDef ? skinDef.name : "Skin";
            const Toast = Swal.mixin({
                toast: true,
                position: "bottom-end",
                showConfirmButton: false,
                timer: 2200,
                timerProgressBar: true,
                background: "#1e293b",
                color: "#f8fafc",
            });
            void Toast.fire({
                icon: "success",
                title: `${name} equipada`,
            });
        }
    }
}

